import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';

// Import the type definition from App.tsx or redefine it here if preferred
// Assuming the types are defined in App.tsx and potentially exported, 
// or we redefine them for clarity/separation:

type ServiceDetailItem = { 
    text: string; 
    isSubItem?: boolean; 
    isHeader?: boolean; 
};

type Service = {
    icon: React.ElementType;
    title: string;
    description: string;
    details?: ServiceDetailItem[]; // Use the structured type
};

type ServiceCardProps = {
    service: Service;
    index: number;
};

// Generate a range of thresholds for more frequent updates
const thresholds = Array.from({length: 11}, (_, i) => i / 10); // [0, 0.1, 0.2, ..., 1.0]

function ServiceCard({ service, index }: ServiceCardProps) {
    const [isFlipped, setIsFlipped] = useState(false);
    const [supportsHover, setSupportsHover] = useState(true);
    const [hasPeeked, setHasPeeked] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const peekTimer1 = useRef<NodeJS.Timeout | null>(null);
    const peekTimer2 = useRef<NodeJS.Timeout | null>(null);

    // Effect 1: Detect first visibility, set hasPeeked, AND run timers
    useEffect(() => {
        const currentCardRef = cardRef.current;
        if (!currentCardRef) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasPeeked) { // Add !hasPeeked check here for safety
                    setHasPeeked(true);
                    observer.unobserve(currentCardRef); // Stop observing visibility

                    // --- START TIMERS HERE ---
                    const staggerDelay = index * 250; 
                    const flipDelay = 1500 + staggerDelay;
                    const unflipDelay = 2700 + staggerDelay;
                    
                    // Clear any previous timers just in case (e.g., fast scroll)
                    if (peekTimer1.current) clearTimeout(peekTimer1.current);
                    if (peekTimer2.current) clearTimeout(peekTimer2.current);

                    peekTimer1.current = setTimeout(() => {
                        setIsFlipped(true); // Flip
                    }, flipDelay);
                    
                    peekTimer2.current = setTimeout(() => {
                        setIsFlipped(false); // Unflip
                        peekTimer1.current = null; // Reset timer refs after completion
                        peekTimer2.current = null;
                    }, unflipDelay); 
                    // --- END TIMERS HERE ---
                }
            },
            { threshold: 0.1 } 
        );
        
        observer.observe(currentCardRef);

        // Cleanup function
        return () => {
            if (currentCardRef) {
                 observer.unobserve(currentCardRef);
            }
            // Also clear timers on unmount
            if (peekTimer1.current) clearTimeout(peekTimer1.current);
            if (peekTimer2.current) clearTimeout(peekTimer2.current);
            peekTimer1.current = null;
            peekTimer2.current = null;
        }; 
    // Keep dependencies as empty array, index is used within the callback scope
    // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []); 

    // Toggle flip state (Keep timer clearing logic here for manual interactions)
    const handleToggleFlip = () => {
        if (peekTimer1.current) clearTimeout(peekTimer1.current);
        if (peekTimer2.current) clearTimeout(peekTimer2.current);
        peekTimer1.current = null; 
        peekTimer2.current = null;
        setIsFlipped(prevState => !prevState);
    };

    // Hover handlers - Now always potentially active, check inside remains
    const handleMouseEnter = () => {
        // console.log(`[${service.title}] Mouse Enter Triggered. Supports Hover: ${supportsHover}`);
        if (supportsHover) { // This check technically becomes always true now
            // console.log(`[${service.title}] Applying Hover Flip.`);
            if (peekTimer1.current) clearTimeout(peekTimer1.current);
            if (peekTimer2.current) clearTimeout(peekTimer2.current);
            peekTimer1.current = null;
            peekTimer2.current = null;
            setIsFlipped(true);
        }
    };
    const handleMouseLeave = () => {
        // console.log(`[${service.title}] Mouse Leave Triggered. Supports Hover: ${supportsHover}`);
        if (supportsHover) { // This check technically becomes always true now
            // console.log(`[${service.title}] Applying Hover Unflip.`);
            if (peekTimer1.current) clearTimeout(peekTimer1.current);
            if (peekTimer2.current) clearTimeout(peekTimer2.current);
            peekTimer1.current = null;
            peekTimer2.current = null;
            setIsFlipped(false);
        }
    };

    return (
        <div 
            ref={cardRef} 
            className="[perspective:1000px] group h-full"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                onClick={handleToggleFlip}
                className={`cursor-pointer relative w-full h-full rounded-2xl shadow-lg transition-transform duration-700 ease-in-out [transform-style:preserve-3d] ${isFlipped ? '[transform:rotateY(180deg)]' : ''}`}
            >
                <div className="w-full bg-salus-branco p-8 rounded-2xl [backface-visibility:hidden] flex flex-col justify-start h-full">
                    <div className="flex-grow">
                        <service.icon className="w-16 h-16 text-salus-verde-escuro mb-6 flex-shrink-0" />
                        <h3 className="text-2xl font-semibold mb-4 text-salus-azul-escuro font-heading flex-shrink-0">{service.title}</h3>
                        <p className="text-salus-azul-escuro opacity-90 text-sm flex-grow">
                            {service.description}
                        </p>
                    </div>
                    <div className="pt-4 border-t border-salus-branco-secundario mt-auto flex items-center justify-between">
                        <span className="text-salus-verde-escuro text-sm font-semibold">
                            Veja mais aqui
                        </span>
                        <ArrowRight className="w-4 h-4 text-salus-verde-escuro" />
                    </div>
                </div>

                <div className="absolute inset-0 w-full h-full bg-salus-azul-escuro text-salus-branco-secundario p-8 rounded-2xl [backface-visibility:hidden] [transform:rotateY(180deg)] flex flex-col justify-start overflow-hidden">
                    <div>
                        <h3 className="text-xl font-semibold mb-4 text-salus-branco font-heading flex-shrink-0">{service.title}</h3>
                    </div>
                    {service.details && service.details.length > 0 && (
                        <ul className="space-y-2 text-sm list-disc list-inside flex-grow my-4 pr-2">
                            {service.details.map((detail, i) => {
                                // Determine conditional classes based on detail type
                                let liClasses = "";
                                let textPrefix = "";
                                if (detail.isHeader) {
                                    liClasses = "list-none -ml-4 font-semibold mt-2"; // Remove bullet, pull left, add margin top
                                } else if (detail.isSubItem) {
                                    liClasses = "ml-4"; // Indent sub-items
                                } else {
                                    // Default item, no extra classes needed unless specified
                                }

                                return (
                                    <li key={i} className={liClasses}>
                                        {detail.text}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <div className="pt-4 border-t border-salus-verde-escuro/30 mt-auto">
                        <button
                            className="text-salus-verde-claro font-semibold flex items-center gap-2 hover:text-salus-branco w-full justify-between text-sm cursor-pointer"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Voltar</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ServiceCard; 